import { Component, OnInit, Input, inject } from "@angular/core";
import { ThirdPartyReviewModel } from "@models/thirdparty/ThirdpartyReviewModel";
import { ThirdPartyModel } from "@models/thirdparty/ThirdpartyModel";
import { GenericListConfig } from "@app/shared/models/GenericList/GenericList";
import { GenericListFieldType } from "@app/shared/models/GenericList/GenericListField";
import { ThirdPartyReviewScoreModel } from "@models/thirdparty/ThirdpartyReviewScoreModel";
import { of } from "rxjs";
import { UserService } from "@app/user/user-profile/shared/user.service";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { StandingdataDataService } from "@app/standingdata/shared/standingdata.service";
import { StandingDataType } from "@enums/StandingDataType";
import { GenericListManagerComponent } from "@app/shared/generic-managers/generic-list-manager/generic-list-manager.component";
import { injectTranslation } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/thirdparty/i18n/scope";

@Component({
    selector: "thirdparty-reviews",
    templateUrl: "thirdparty-review.component.html",
    styleUrls: ["thirdparty-review.component.scss"],
    imports: [GenericListManagerComponent],
})
export class ThirdPartyReviewComponent implements OnInit {
    private _sdDataService = inject(StandingdataDataService);
    private _userService = inject(UserService);
    private _promptService = inject(CerrixPromptService);
    private _translation = injectTranslation(scope);

    @Input() thirdpartyModel: ThirdPartyModel;
    @Input() canReview: boolean;

    reviews: ThirdPartyReviewModel[];
    isLoading = true;

    scores: ThirdPartyReviewScoreModel[];
    reviewConfig: GenericListConfig;

    async ngOnInit() {
        if (!this.thirdpartyModel && !this.thirdpartyModel.Guid) {
            return;
        }
        await this._translation.ready();
        this.initReviewConfig();
    }

    initReviewConfig() {
        this.reviewConfig = <GenericListConfig>{
            idProp: "id",
            uiidAdd: "thirdpartyreview-addbutton",
            uiidDelete: "thirdpartyreview-deletebutton",
            name: this._translation.t("reviews.listName"),
            isSortable: false,

            allowAdd: this.canReview,
            allowDelete: this.canReview,
            allowEdit: this.canReview,

            data: this.thirdpartyModel.Reviews,
            overviewSortBy: "createdAt",
            overviewSortAsc: false,

            fields: [
                {
                    fieldName: "scoreId",
                    prettyName: this._translation.t("reviews.scoreField"),
                    uiid: "thirdpartyreview-score",
                    fieldType: GenericListFieldType.ColoredSelect,
                    required: true,
                    editorWidth: "12",
                    getDataMethod: () => {
                        return this._sdDataService.getAllByType(StandingDataType.TpThirdPartyScore);
                    },
                },
                {
                    fieldName: "comment",
                    prettyName: this._translation.t("reviews.commentField"),
                    uiid: "thirdpartyreview-comment",
                    required: true,
                    fieldType: GenericListFieldType.TextArea,
                    editorWidth: "12",
                },
                {
                    fieldName: "createdBy",
                    prettyName: this._translation.t("reviews.reviewerField"),
                    fieldType: GenericListFieldType.Text,
                    hideInEdit: true,
                },
                {
                    fieldName: "createdAt",
                    prettyName: this._translation.t("reviews.dateField"),
                    fieldType: GenericListFieldType.Date,
                    hideInEdit: true,
                },
            ],
            add: async (row: ThirdPartyReviewModel) => {
                this._userService.getUserName().then((username) => (row.createdBy = username));
                // This is set at server side
                row.createdAt = new Date();
                return true;
            },
            delete: async (row: ThirdPartyReviewModel) => {
                return await this._promptService
                    .confirm(
                        this._translation.t("reviews.deleteTitle"),
                        this._translation.t("reviews.deleteMessage"),
                    )
                    .toPromise();
            },

            dataChanged: (data) => {},
        };
    }

    editReview() {
        return of(this.thirdpartyModel.Reviews);
    }

    async saveReview(data: ThirdPartyReviewModel) {
        const model = new ThirdPartyReviewModel();

        model.comment = data.comment;
        model.scoreId = data.scoreId;
        model.score = data.score;

        if (data.id > 0) {
            model.id = data.id;
        } else {
            model.id = 0;
        }

        this.thirdpartyModel.Reviews.push(model);
    }
}
